/**
 * Firestore setup and the raw read/write primitives.
 *
 * A shared table is one document per slice rather than one document for
 * everything:
 *
 *   tables/{CODE}                    metadata
 *   tables/{CODE}/slices/roster      { people, groups }
 *   tables/{CODE}/slices/schedule    { folders, events }
 *   tables/{CODE}/slices/settings    { ... }
 *   tables/{CODE}/slices/attendance  { "personId-eventId": statusId }
 *
 * v1 wrote all of it as a single `setDoc` every 30 seconds, so whoever stopped
 * typing last erased everyone else's work. Splitting by slice means editing the
 * roster cannot clobber attendance, and writing attendance with `{ merge: true }`
 * means marking one cell cannot clobber the cell next to it.
 */

import { initializeApp } from 'firebase/app';
import { ReCaptchaV3Provider, initializeAppCheck } from 'firebase/app-check';
import {
  deleteField,
  doc,
  getDoc,
  initializeFirestore,
  onSnapshot,
  persistentLocalCache,
  persistentMultipleTabManager,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { SLICE_NAMES, toSlices } from './slices';

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/** Sharing is optional. With no config the app runs entirely on localStorage. */
export const isFirebaseConfigured = Boolean(config.apiKey && config.projectId);

/**
 * App Check, if a reCAPTCHA v3 site key is configured.
 *
 * Table codes are unauthenticated, so without this the Firestore endpoints can
 * be driven by anything holding the public web config — a cost problem rather
 * than a data-exposure one, since a caller still has to guess a code to reach
 * a real table. Attaching App Check makes requests prove they came from this
 * app. It is opt-in because it needs a key registered in the Firebase console;
 * with none set, nothing changes.
 */
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;

let db = null;

function database() {
  if (!isFirebaseConfigured) return null;
  if (!db) {
    const app = initializeApp(config);

    if (appCheckSiteKey) {
      try {
        initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(appCheckSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (error) {
        // A bad key must not take sharing down with it.
        // eslint-disable-next-line no-console
        console.warn('App Check could not start; continuing without it.', error);
      }
    }

    // The offline cache is what lets marks made on bad venue wifi survive and
    // send themselves later, instead of being lost on reload. The multi-tab
    // manager matters because having the table open in two tabs is normal, and
    // the single-tab default leaves the second tab without persistence.
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  }
  return db;
}

const tableRef = (code) => doc(database(), 'tables', code);
const sliceRef = (code, slice) => doc(database(), 'tables', code, 'slices', slice);

/**
 * Reads every slice at once, for joining a table the first time.
 *
 * Each slice is fetched by name rather than by listing the collection, because
 * `firestore.rules` denies `list` — that is what stops someone walking the
 * `tables` collection to harvest codes.
 *
 * Tables created before the split live entirely in the `tables/{CODE}` document
 * and have no slices; those come back as `legacy` and are converted on read.
 * The first write after that lays down the slices.
 */
export async function fetchTable(code) {
  if (!isFirebaseConfigured) return null;
  const meta = await getDoc(tableRef(code));
  if (!meta.exists()) return null;

  const snapshots = await Promise.all(SLICE_NAMES.map((slice) => getDoc(sliceRef(code, slice))));
  const result = { meta: meta.data() };
  snapshots.forEach((snapshot, index) => {
    if (snapshot.exists()) result[SLICE_NAMES[index]] = snapshot.data();
  });
  return result;
}

/** Creates the table document and writes the first full snapshot of each slice. */
export async function createTable(code, table) {
  if (!isFirebaseConfigured) throw new Error('Sharing is not configured');
  await setDoc(tableRef(code), {
    version: table.version,
    name: table.settings?.name || null,
    createdAt: serverTimestamp(),
    lastUpdated: serverTimestamp(),
  });
  const slices = toSlices(table);
  await Promise.all(SLICE_NAMES.map((slice) => writeSlice(code, slice, slices[slice])));
}

/** Replaces a whole slice. Used for roster, schedule and settings. */
export function writeSlice(code, slice, data) {
  return setDoc(sliceRef(code, slice), data);
}

/**
 * Writes only the cells that changed. `null` deletes the cell. Two people
 * marking different rows of the same event both land, because Firestore merges
 * per field rather than replacing the document.
 */
export function writeCells(code, cells) {
  const payload = {};
  for (const [key, value] of Object.entries(cells)) {
    payload[key] = value === null ? deleteField() : value;
  }
  return setDoc(sliceRef(code, 'attendance'), payload, { merge: true });
}

/**
 * Stamps the parent document, and mirrors the name onto it so the table can be
 * identified without reading its slices.
 */
export function touchTable(code, name) {
  const payload = { lastUpdated: serverTimestamp() };
  if (typeof name === 'string' && name.trim()) payload.name = name.trim();
  return setDoc(tableRef(code), payload, { merge: true });
}

/**
 * Subscribes to one slice: `onData(data, hasPendingWrites, fromCache)`.
 *
 * `hasPendingWrites` means the snapshot has this browser's unacknowledged
 * writes laid over it — for a whole-document slice, an echo that must not be
 * merged back over edits made since. `fromCache` means it came from the local
 * cache rather than the server.
 *
 * `includeMetadataChanges` is only for attendance: without it, a snapshot whose
 * data is unchanged but whose writes have just been acknowledged raises no
 * event, so the server's own state may never be seen in a session. Attendance
 * diffs every snapshot, so an unchanged one is harmless there. The whole-slice
 * documents must not have it: the acknowledged state would be merged over edits
 * made since and not yet sent.
 */
export function subscribeSlice(code, slice, onData, onError, { includeMetadataChanges = false } = {}) {
  if (!isFirebaseConfigured) return () => {};
  return onSnapshot(
    sliceRef(code, slice),
    { includeMetadataChanges },
    (snapshot) => {
      if (!snapshot.exists()) return;
      onData(snapshot.data(), snapshot.metadata.hasPendingWrites, snapshot.metadata.fromCache);
    },
    onError
  );
}
